import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BwjPageContent } from '../../../../../framework/components/bwj-page-content';
import { TimingMonitorHeader } from '../../../components/time-monitor-header';
import { getUtcNowUnixTimestamp, MINUTE_IN_MILLISECONDS, SECOND_IN_MILLISECONDS } from '../../../../../framework/utils/date-time';
import { ITimingSpecComponentModel } from '../../../../markdown/parser/timing-spec';
import { firstOrDefault, forEach, sum, where } from '../../../../../framework/utils/collection';
import { TimingMode } from '../../../../../entities/timing-mode';
import * as formService from '../../../form-services/presentation-editor';
import { PresentationTiming } from '../../../../../entities-generated/common/presentation-timing';
import { MarkdownViewport } from '../../../../markdown/components/viewport';
import { PresentationApiClient } from '../../../../../services/presentation';
import { useToast } from '../../../../../framework/components/bwj-toast/context';
import { useNavigator } from '../../../../../framework/services/navigation';
import { AppRoutes } from '../../../../app-routes';
import { MessageBarType } from '@fluentui/react';
import { xlate, xlationContext } from '../../../../../framework/utils/i18n';
import { TimingState } from '../../../../../entities/timing-state';
import { IPresenterAccountPreferences } from '../../../../../entities-generated/common/presenter-account-preferences';
import { BwjBillboard } from '../../../../../framework/components/bwj-billboard';
import { useReffedState } from '../../../../../framework/hooks/reffed-state';

xlationContext('In-progress discussion page');

interface IPresentationInflightProps {
  accountPreferences: IPresenterAccountPreferences;
}

interface ISectionDuration {
  id: number;
  timingMode: TimingMode;
  duration: number; // in milliseconds
}

const timeUnitsToMs = (primaryUnit: number, secondaryUnit: number) => {
  const value = (primaryUnit * MINUTE_IN_MILLISECONDS) + (secondaryUnit * SECOND_IN_MILLISECONDS);
  return value;
}

const applyComputedDuration = (msDuration: number, section: ITimingSpecComponentModel) => {
  const totalSeconds = Math.round(msDuration / 1000);
  section.computedPrimaryUnits = Math.floor(totalSeconds / 60);
  section.computedSubUnits = totalSeconds % 60;
}

const getMillisecondsPerShare = (presentationDuration: number, timingSections: ITimingSpecComponentModel[]) => {
  const relevantSections = where(timingSections, s => s.specType !== TimingMode.Preamble);
  const timedSections = where(relevantSections, s => s.specType !== TimingMode.ShareTime);
  const definedTime = sum(timedSections, s => timeUnitsToMs(s.primaryUnits, s.subUnits!));
  const sharableTime = presentationDuration - definedTime;

  const shareSections = where(relevantSections, s => s.specType === TimingMode.ShareTime);
  const totalShares = sum(shareSections, s => s.primaryUnits);
  const mps = sharableTime > 0 ? Math.floor(sharableTime / totalShares) : 1;

  return mps;
}

const getSectionDurations = (sections: ITimingSpecComponentModel[], msPerShare: number) => {
  const relevantSections = where(sections, s => s.specType !== TimingMode.Preamble);
  const durations = relevantSections.map(s => {
    const d = s.specType === TimingMode.ShareTime ? s.primaryUnits * msPerShare : timeUnitsToMs(s.primaryUnits, s.subUnits!);
    const duration: ISectionDuration = {
      id: s.startLineNumber,
      timingMode: s.specType,
      duration: d,
    };
    return duration;
  });

  return durations;
}

const getSectionDurationScaleFactor = (currentSection: ITimingSpecComponentModel, durations: ISectionDuration[], timeAvailableMs: number) => {
  const remainingFlexDurations = where(durations, s => s.id >= currentSection.startLineNumber && s.timingMode !== TimingMode.FirmTime);
  const plannedFlexTime = sum(remainingFlexDurations, s => s.duration);

  const remainingFirmDurations = where(durations, s => s.id >= currentSection.startLineNumber && s.timingMode === TimingMode.FirmTime);
  const remainingFirmTime = sum(remainingFirmDurations, s => s.duration);
  const availableFlexTime = timeAvailableMs - remainingFirmTime;

  let scaleFactor = availableFlexTime > 0 && plannedFlexTime > 0 ? availableFlexTime / plannedFlexTime : 0;
  return scaleFactor;
}

const updateComputedDurations = (
  durationScaleFactor: number,
  currentSection: ITimingSpecComponentModel,
  sections: ITimingSpecComponentModel[],
  durations: ISectionDuration[]) => {

  const updatable = where(sections, s => s.startLineNumber >= currentSection.startLineNumber 
    && (s.specType === TimingMode.FlexTime || s.specType === TimingMode.ShareTime));
  forEach(updatable, section => {
    const duration = firstOrDefault(durations, d => d.id === section.startLineNumber)!;
    applyComputedDuration(duration.duration * durationScaleFactor, section);
  });
}

const subscriberId = 'PartialPage.PresentationInflight';

export const PresentationInflight: React.FunctionComponent<IPresentationInflightProps> = (props) => {
  const formModel = formService.useFormModel(subscriberId);
  const {
    duration: modelDuration,
    actualDuration: modelActualDuration,
    currentSection, currentSectionStart,
    endTime: discussionEndTime,
    timing: modelTiming
  } = formModel;

  // the original sections from the form model.  Because we treat the form model as read only, it is not appropriate to change these
  const immutableSections = useMemo(() => formModel.componentModel as ITimingSpecComponentModel[], [formModel]);

  // immutable
  const presentationDuration = useMemo(() => {
    return modelDuration !== undefined ? (modelDuration * 1000) : (modelActualDuration ?? 1000);
  }, [modelDuration, modelActualDuration]);

  // immutable. Computed value in milliseconds of one 'time share', based on the time available
  const msPerShare = useMemo(() => {
    return getMillisecondsPerShare(presentationDuration, immutableSections);
  }, [immutableSections, presentationDuration]);

  // mutable copy of the original form model sections
  const [sections, setSections, sectionsRef] = useReffedState<ITimingSpecComponentModel[]>(immutableSections.map(s => {
    const section = {...s};
    section.hideNotes = true;
    if(section.specType === TimingMode.ShareTime) {
      applyComputedDuration(section.primaryUnits * msPerShare, section);
    }
    return section;
  }));

  // Immutable.  Computed duration of each section based on the actual time available for the discussion when it started.
  // these values are scaled during the presentation as needed
  const sectionDurations = useMemo(() => {
    return getSectionDurations(immutableSections, msPerShare);
  }, [immutableSections, msPerShare]);

  // coefficient used to scale flexible section durations based on actual time available
  // Changes when current section changes
  const flexDurationScale = useMemo(() => {
    const timeRemaining = discussionEndTime - currentSectionStart;
    return getSectionDurationScaleFactor(currentSection, sectionDurations, timeRemaining);
  }, [discussionEndTime, currentSectionStart, currentSection, sectionDurations]);

  // for scenarios where there is nothing but fixed time duration sections, this is supposed to tell you how ahead/behind you are overall.
  // This would be the [sum of all sections before current section] - [time when the current section actually started].
  // On first section in this scenario, this is undefined
  const [projectedTimeDelta_seconds, setProjectedTimeDelta_seconds] = useState<number | undefined>(undefined);

  const { showToast } = useToast();
  const navigator = useNavigator();

  // clears any outstanding timing state change timeouts
  const timingStateChangeId = useRef(0);
  useEffect(() => {
    return() => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      window.clearTimeout(timingStateChangeId.current);
    };
  }, []);

  // this is invoked when the current section changes
  useEffect(() => {
    const mySections = sectionsRef.current;

    // swap current section
    const formerSection = firstOrDefault(mySections, s => s.timingState !== TimingState.Idle);
    if(!!formerSection) { formerSection.timingState = TimingState.Idle; }
    const cs = firstOrDefault(mySections, s => s.startLineNumber === currentSection.startLineNumber);
    if(!!cs) { 
      cs.timingState = currentSection !== formerSection ? TimingState.Active : currentSection.timingState;
      cs.hideNotes = false;
    }

    // use the scaling factor to project durations of all sections from (inclusive) current section down
    updateComputedDurations(flexDurationScale, currentSection, mySections, sectionDurations);

    setSections([...mySections]);
  }, [flexDurationScale, currentSection, sectionDurations,
    // below only here for sake of compiler
    sectionsRef, setSections]);

  // the time allotted to the current section
  // only changes when current section changes
  const timeAllotted_ms = useMemo(() => {
    const cs = firstOrDefault(sectionsRef.current, s => s.startLineNumber === currentSection.startLineNumber)!;
    if(currentSectionStart > discussionEndTime) { return 0; }

    if(modelTiming === PresentationTiming.None) { return presentationDuration; }

    const timeRemaining = discussionEndTime - currentSectionStart;
    const duration = firstOrDefault(sectionDurations, d => d.id === cs.startLineNumber)!;

    // although firm time does not scale, it will still be truncated if its duration will take the presentation past the set end time
    if(cs.specType === TimingMode.FirmTime) {
      return duration.duration < timeRemaining ? duration.duration : timeRemaining;
    }

    const scaledTime = Math.round(duration.duration * flexDurationScale);
    return scaledTime;
  }, [sectionsRef, currentSection, currentSectionStart, discussionEndTime, presentationDuration, modelTiming, sectionDurations, flexDurationScale]);

  const onSectionClick = (section: ITimingSpecComponentModel) => {
    if(formModel.currentSection === section) { return; }

    const now = getUtcNowUnixTimestamp();
    const startTime = now < formModel.endTime ? now : formModel.endTime;
    formService.currentSectionStart.setInputValue(startTime);
    formService.currentSection.onUpdate(section);

    const id = formService.id.getOutputValue();
    const request = formService.toNoteActivePresentationSectionRequest();
    PresentationApiClient.noteActivePresentationSection(id, request)
    .catch(() => {}); // failure ignored
  };

  const onToggleSectionNotes = (section: ITimingSpecComponentModel) => {
    section.hideNotes = !section.hideNotes;
    setSections([...sections]);
  }

  const onTimingStateChanged = (state: TimingState) => {
    timingStateChangeId.current = window.setTimeout(() => {
      const section = formModel.currentSection;
      section.timingState = state;
      setSections([...sections]);
    }, 50);
  }

  const onOvertimeTick = useCallback(() => {
    const mySections = sectionsRef.current;
    const now = getUtcNowUnixTimestamp();
    const timeRemaining = discussionEndTime - now;
    const nextSection = firstOrDefault(mySections, s => s.startLineNumber > currentSection.startLineNumber);
    if(!nextSection) { return; }

    const scale = getSectionDurationScaleFactor(nextSection, sectionDurations, timeRemaining);
    // use the scaling factor to project durations of all sections starting from next section down
    updateComputedDurations(scale, currentSection, mySections, sectionDurations);
    setSections([...mySections]);
  }, [sectionsRef, setSections, discussionEndTime, currentSection, sectionDurations]);

  const onEndDiscussion = () => {
    PresentationApiClient.stopPresentation(formService.id.getOutputValue())
    .then(() => {
      navigator.goTo(AppRoutes.myPresentations.url());
    })
    .catch(() => {
      showToast(MessageBarType.error, xlate('An error occurred, try again'))
    });
  };

  return (
    <>
      <TimingMonitorHeader
        timeAllotted_ms={timeAllotted_ms}
        projectedTimeDelta_seconds={projectedTimeDelta_seconds}
        warningColorThreshold={(100 - props.accountPreferences.warningColorAfterPercentElasped) / 100}
        onTimingStateChanged={onTimingStateChanged}
        onOvertimeTick={onOvertimeTick}
        accountSettings={props.accountPreferences}
      />
      <BwjPageContent
        contentDescription='primary content (no subcontent)'
        style={{ padding: 0, }}
      >
        {sections.length > 0 ?
        <MarkdownViewport
          componentModel={sections}
          baseFontSize={props.accountPreferences.fontSizePreference}
          onSectionHeadingClick={onSectionClick}
          onEndDiscussion={onEndDiscussion}
          onToggleSectionNotes={onToggleSectionNotes}
        />
        :
        <BwjBillboard
          icon={'FileComment'}
          message={xlate('This discussion plan has no content')}
        />}
      </BwjPageContent>
    </>
  );
};
